import { useEffect, useMemo, useState } from "react";
import api, { API, getStaffToken } from "../../lib/api";
import { format, parseISO } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { Anchor, FileDown, FileSpreadsheet, FileText, ChevronLeft, ChevronRight, Ship, Clock, CheckCircle2, Activity, Users, Download } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const PERIODS = [
  { id: "day", label: "Jour" },
  { id: "week", label: "Semaine" },
  { id: "month", label: "Mois" },
];

const STATUS_OPTIONS = [
  { id: "", label: "Tous" },
  { id: "programmé", label: "Programmées" },
  { id: "en_cours", label: "En cours" },
  { id: "terminé", label: "Terminées" },
];

const STATUS_BADGE = {
  programmé: { label: "Programmée", color: "bg-[#FAFAF7] text-[#0A0A0A]/65 border-[#0A0A0A]/15" },
  en_cours: { label: "En cours", color: "bg-orange-50 text-orange-700 border-orange-200" },
  terminé: { label: "Terminée", color: "bg-green-50 text-green-700 border-green-200" },
};

function shiftRef(period, ref, delta) {
  const d = parseISO(ref);
  if (period === "day") d.setDate(d.getDate() + delta);
  if (period === "week") d.setDate(d.getDate() + delta * 7);
  if (period === "month") d.setMonth(d.getMonth() + delta);
  return d.toISOString().slice(0, 10);
}

function Kpi({ icon: Icon, label, value, color = "#B8922A", sub }) {
  return (
    <div className="bg-white border border-[#0A0A0A]/8 p-4 sm:p-5">
      <div className="flex items-start justify-between mb-2">
        <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">{label}</div>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="font-display-serif text-2xl sm:text-3xl text-[#0A0A0A] leading-none">{value}</div>
      {sub && <div className="text-[0.65rem] text-[#0A0A0A]/45 mt-1.5">{sub}</div>}
    </div>
  );
}

export default function StaffTraverseesHistory() {
  const today = new Date().toISOString().slice(0, 10);
  const [period, setPeriod] = useState("week");
  const [ref, setRef] = useState(today);
  const [status, setStatus] = useState("");
  const [bateauId, setBateauId] = useState("");
  const [startedOnly, setStartedOnly] = useState(false);
  const [bateaux, setBateaux] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // Load bateaux list once for the filter dropdown
  useEffect(() => {
    api.get("/staff/bateaux")
      .then((r) => setBateaux(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBateaux([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ period, date: ref });
    if (status) params.set("status", status);
    if (bateauId) params.set("bateau_id", bateauId);
    if (startedOnly) params.set("started_only", "true");
    api.get(`/staff/traversees/history?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [period, ref, status, bateauId, startedOnly]);

  const downloadReport = async (format) => {
    setDownloading(true);
    try {
      const token = getStaffToken();
      const params = new URLSearchParams({ period, date: ref });
      if (status) params.set("status", status);
      if (bateauId) params.set("bateau_id", bateauId);
      // Exports default to en_cours+terminé (started_only=true) unless an
      // explicit status filter is active. This matches the brief: extract
      // only crossings that have effectively departed.
      if (!status) params.set("started_only", "true");
      const url = `${API}/staff/traversees/history/report.${format}?${params}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        toast.error(`Export ${format.toUpperCase()} impossible`);
        return;
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `bbr-traversees-${period}-${ref}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Rapport ${format.toUpperCase()} téléchargé`);
    } finally {
      setDownloading(false);
    }
  };
  const downloadPdf = () => downloadReport("pdf");
  const downloadXlsx = () => downloadReport("xlsx");

  const chartData = useMemo(() => {
    if (!data) return [];
    if (period === "day") {
      // Group by hour-ish from depart_time
      const buckets = {};
      data.items.forEach((it) => {
        const k = it.depart_time || "—";
        buckets[k] = (buckets[k] || 0) + 1;
      });
      return Object.entries(buckets).map(([k, v]) => ({ label: k, count: v })).sort((a, b) => a.label.localeCompare(b.label));
    }
    // week / month: by_day already aggregated
    return (data.by_day || []).map((d) => ({
      label: format(parseISO(d.date), period === "week" ? "EEE d" : "d MMM", { locale: frLocale }),
      count: d.total,
      terminé: d["terminé"] || 0,
    }));
  }, [data, period]);

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="staff-traversees-history">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
        <h1 className="font-display-serif text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A]">Historique des traversées</h1>
        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
          <button
            onClick={downloadXlsx}
            disabled={downloading || !data}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#0A0A0A] border border-[#0A0A0A]/20 text-[0.7rem] uppercase tracking-[0.22em] hover:border-[#B8922A] hover:text-[#B8922A] transition-colors disabled:opacity-50"
            data-testid="download-xlsx-btn"
          >
            <FileSpreadsheet size={13} /> {downloading ? "…" : "Export Excel"}
          </button>
          <button
            onClick={downloadPdf}
            disabled={downloading || !data}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#B8922A] text-white text-[0.7rem] uppercase tracking-[0.22em] hover:bg-[#9d7a23] transition-colors disabled:opacity-50"
            data-testid="download-pdf-btn"
          >
            <FileDown size={13} /> {downloading ? "…" : "Rapport PDF"}
          </button>
        </div>
      </div>
      <p className="text-sm text-[#0A0A0A]/55 mb-6">Vue agrégée des traversées (programmées, en cours, terminées).</p>

      {/* Period selector */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="period-selector">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={`px-3 sm:px-4 py-2 text-[0.65rem] sm:text-[0.7rem] uppercase tracking-[0.22em] border transition-all ${
              period === p.id
                ? "bg-[#B8922A] text-white border-[#B8922A]"
                : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#B8922A]"
            }`}
            data-testid={`period-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Status filter + date navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => setRef(shiftRef(period, ref, -1))} className="p-2 border border-[#0A0A0A]/15 hover:border-[#B8922A]" data-testid="ref-prev">
            <ChevronLeft size={14} />
          </button>
          <input
            type="date"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            className="border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] focus:outline-none"
            data-testid="ref-date"
          />
          <button onClick={() => setRef(shiftRef(period, ref, 1))} className="p-2 border border-[#0A0A0A]/15 hover:border-[#B8922A]" data-testid="ref-next">
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.id || "all"}
              onClick={() => setStatus(s.id)}
              className={`px-3 py-1.5 text-[0.62rem] uppercase tracking-[0.22em] border transition-all ${
                status === s.id
                  ? "bg-[#B8922A] text-white border-[#B8922A]"
                  : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#B8922A]"
              }`}
              data-testid={`status-filter-${s.id || "all"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Second filter row — boat + "started only" toggle, kept distinct for legibility */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Bateau :</span>
        <select
          value={bateauId}
          onChange={(e) => setBateauId(e.target.value)}
          className="px-3 py-1.5 border border-[#0A0A0A]/15 focus:border-[#B8922A] focus:outline-none text-sm bg-white min-w-[200px]"
          data-testid="bateau-filter"
        >
          <option value="">— Tous les bateaux —</option>
          {bateaux.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] text-[#0A0A0A]/65 cursor-pointer ml-2">
          <input
            type="checkbox"
            checked={startedOnly}
            onChange={(e) => setStartedOnly(e.target.checked)}
            className="accent-[#B8922A]"
            data-testid="started-only-toggle"
          />
          Uniquement les traversées départies
        </label>
        {(bateauId || startedOnly) && (
          <button
            onClick={() => { setBateauId(""); setStartedOnly(false); }}
            className="text-[0.65rem] uppercase tracking-[0.18em] text-[#0A0A0A]/55 hover:text-[#B8922A]"
            data-testid="clear-extra-filters"
          >
            ✕ Réinitialiser
          </button>
        )}
      </div>

      {data && (
        <div className="mb-6 text-sm text-[#0A0A0A]/70" data-testid="history-label">
          {data.label}
          <span className="text-[#0A0A0A]/45 ml-2">· {data.total} traversée(s)</span>
        </div>
      )}

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Kpi icon={Ship} label="Total" value={data.total} />
          <Kpi icon={Clock} label="Programmées" value={data.by_status["programmé"] || 0} color="#9CA3AF" />
          <Kpi icon={Activity} label="En cours" value={data.by_status["en_cours"] || 0} color="#F97316" />
          <Kpi icon={CheckCircle2} label="Terminées" value={data.by_status["terminé"] || 0} color="#16A34A" />
          <Kpi icon={Users} label="Passagers" value={data.total_guests} sub={`${data.by_direction["aller"] || 0} aller · ${data.by_direction["retour"] || 0} retour`} />
        </div>
      )}

      {/* Chart */}
      {data && chartData.length > 0 && (
        <div className="bg-white border border-[#0A0A0A]/8 p-4 sm:p-5 mb-6">
          <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#B8922A] mb-3">
            {period === "day" ? "Répartition par horaire" : "Évolution journalière"}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#999" />
              <YAxis tick={{ fontSize: 11 }} stroke="#999" allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" fill="#B8922A" name="Total" radius={[2, 2, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill="#B8922A" />
                ))}
              </Bar>
              {period !== "day" && <Bar dataKey="terminé" fill="#16A34A" name="Terminées" radius={[2, 2, 0, 0]} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* By boat */}
      {data && data.by_boat?.length > 0 && (
        <div className="bg-white border border-[#0A0A0A]/8 p-4 sm:p-5 mb-6">
          <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#B8922A] mb-3">Répartition par bateau</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <thead>
                <tr className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 border-b border-[#0A0A0A]/10">
                  <th className="text-left py-2">Bateau</th>
                  <th className="text-center py-2">Total</th>
                  <th className="text-center py-2">Terminées</th>
                  <th className="text-right py-2">Passagers</th>
                </tr>
              </thead>
              <tbody>
                {data.by_boat.map((b) => (
                  <tr key={b.bateau_id} className="border-b border-[#0A0A0A]/5">
                    <td className="py-2 text-[#0A0A0A]">{b.bateau_name}</td>
                    <td className="py-2 text-center">{b.total}</td>
                    <td className="py-2 text-center text-green-700">{b["terminé"]}</td>
                    <td className="py-2 text-right font-medium">{b.guests}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Crossings list */}
      <div className="bg-white border border-[#0A0A0A]/8" data-testid="crossings-list">
        <div className="hidden md:grid grid-cols-14 text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/50 px-5 py-3 border-b border-[#0A0A0A]/10 bg-[#FAFAF7]"
             style={{gridTemplateColumns: "1.4fr 0.7fr 1.3fr 2fr 1.4fr 1fr 1.6fr"}}>
          <div>Date</div>
          <div>Heure</div>
          <div>Direction</div>
          <div>Bateau</div>
          <div>Statut</div>
          <div className="text-right">Passagers</div>
          <div className="text-right">Manifeste</div>
        </div>
        {loading && <div className="p-6 text-sm text-[#0A0A0A]/50">Chargement…</div>}
        {!loading && (!data?.items || data.items.length === 0) && (
          <div className="p-6 text-sm text-[#0A0A0A]/50">Aucune traversée pour cette période.</div>
        )}
        {data?.items?.map((it) => {
          const st = STATUS_BADGE[it.status] || { label: it.status, color: "border-[#0A0A0A]/15" };
          return (
            <div
              key={it.id}
              className="md:grid md:items-center flex flex-col items-start gap-2 md:gap-0 px-5 py-3.5 border-b border-[#0A0A0A]/5 hover:bg-[#FAFAF7] transition-colors"
              style={{gridTemplateColumns: "1.4fr 0.7fr 1.3fr 2fr 1.4fr 1fr 1.6fr"}}
              data-testid={`crossing-row-${it.id}`}
            >
              <div className="text-sm text-[#0A0A0A]">{it.date}</div>
              <div className="text-sm text-[#B8922A] font-medium">{it.depart_time}</div>
              <div className="text-sm text-[#0A0A0A]/70 capitalize">{it.direction}</div>
              <div className="text-sm text-[#0A0A0A]/75">{it.bateau_name}</div>
              <div>
                <span className={`inline-block px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.18em] border ${st.color}`}>{st.label}</span>
              </div>
              <div className="md:text-right text-sm w-full flex md:block items-center justify-between">
                <span className="md:hidden text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/50">Passagers</span>
                <span className="font-medium">{it.guests}</span>
                <span className="text-[0.7rem] text-[#0A0A0A]/45 ml-1">({it.passenger_count} résa.)</span>
              </div>
              {/* iter-103 · Per-crossing manifest exports — CSV / XLSX / PDF.
                  Requested by the user: "toujours pas possible d'exporter la
                  liste des passagers d'un bateau après une traversée". */}
              <div className="w-full md:w-auto md:text-right flex items-center gap-1 md:justify-end">
                <ManifestExportButtons crossingId={it.id} label={`${it.date}-${it.depart_time || "-"}`} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// iter-103 · Manifest export buttons for a single crossing.
// Reuses the existing backend endpoints:
//   GET /api/staff/traversees/{tid}/passengers.{csv,xlsx,pdf}
// Handles token auth via `getStaffToken()` so the browser can download
// blobs while still passing the Bearer header — a plain <a href> would
// hit the endpoint anonymous and 401.
// ═══════════════════════════════════════════════════════════════════════
function ManifestExportButtons({ crossingId, label }) {
  const [busy, setBusy] = useState(null); // holds the format currently downloading
  const download = async (format, mime) => {
    setBusy(format);
    try {
      const token = getStaffToken();
      const res = await fetch(
        `${API}/api/staff/traversees/${crossingId}/passengers.${format}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        toast.error(text?.slice(0, 120) || `Export ${format.toUpperCase()} impossible`);
        return;
      }
      const blob = new Blob([await res.blob()], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `manifeste-${label}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Manifeste ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(e?.message || `Export ${format.toUpperCase()} impossible`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <button
        onClick={() => download("csv", "text/csv")}
        disabled={busy != null}
        className="inline-flex items-center gap-1 px-2 py-1 text-[0.55rem] uppercase tracking-[0.16em] text-[#0A0A0A]/60 hover:text-[#B8922A] disabled:opacity-40"
        data-testid={`export-csv-${crossingId.slice(0, 8)}`}
        title="Exporter la liste des passagers en CSV"
      >
        <FileText size={11} /> {busy === "csv" ? "…" : "CSV"}
      </button>
      <button
        onClick={() => download("xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")}
        disabled={busy != null}
        className="inline-flex items-center gap-1 px-2 py-1 text-[0.55rem] uppercase tracking-[0.16em] text-[#0A0A0A]/60 hover:text-[#B8922A] disabled:opacity-40"
        data-testid={`export-xlsx-${crossingId.slice(0, 8)}`}
        title="Exporter la liste des passagers en Excel"
      >
        <FileSpreadsheet size={11} /> {busy === "xlsx" ? "…" : "Excel"}
      </button>
      <button
        onClick={() => download("pdf", "application/pdf")}
        disabled={busy != null}
        className="inline-flex items-center gap-1 px-2 py-1 text-[0.55rem] uppercase tracking-[0.16em] text-[#0A0A0A]/60 hover:text-[#B8922A] disabled:opacity-40"
        data-testid={`export-pdf-${crossingId.slice(0, 8)}`}
        title="Exporter la liste des passagers en PDF"
      >
        <FileDown size={11} /> {busy === "pdf" ? "…" : "PDF"}
      </button>
    </>
  );
}
